import Cookie from 'js-cookie';
import idx from 'idx';
import moment from 'moment';
import {
	toArray,
	includes,
	groupBy,
	toPairs,
	map,
	sortBy,
	startsWith,
	partition,
	some,
} from 'lodash';
import { JWT_TOKEN_KEY, MS_JWT_TOKEN_EXPIRATION, GROUP_IDS } from './constants';

export const concatStrings = (x, y) => x + ' ' + y;

export const capitalize = str => str.charAt(0).toUpperCase() + str.slice(1);

export const capitalizeWords = str =>
	str
		.split(' ')
		.map(w => capitalize(w))
		.join(' ');

export const getName = (user, useUsernameAsFallback = true) => {
	if (!user) return null;

	if (useUsernameAsFallback && (!user.first_name && !user.last_name)) {
		return user.username;
	}

	return capitalizeWords(`${user.first_name} ${user.last_name}`);
};

export function validateYYYYMMDDFormat(dateString) {
	var regEx = /^\d{4}-\d{2}-\d{2}$/;

	return dateString.match(regEx) != null;
}

export const timeSince = date => moment(date).fromNow();

export const formatDate = (
	timestamp,
	withTime = false,
	timezoneOffset = false
) => {
	const ts = moment(timestamp);

	const format = withTime ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD';

	return ts.format(format);
};

export const convertToUTC = date => {
	return new Date(
		date.getUTCFullYear(),
		date.getUTCMonth(),
		date.getUTCDate(),
		date.getUTCHours(),
		date.getUTCMinutes(),
		date.getUTCSeconds()
	);
};

export const entityToOptions = (entity, labelField = 'name') =>
	toArray(entity).map(item => ({
		value: item.id,
		label: item[labelField],
	}));

export const protectedAsset = fileUrl => {
	const token = Cookie.get(JWT_TOKEN_KEY);
	const url = fileUrl;

	return url + '?token=' + token;
};

export function minutesToMs(x) {
	return x * 60 * 1000;
}

export function validateEmail(email) {
	const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

	return re.test(email);
}

export function getQueryVariable(variable) {
	const query = window.location.search.substring(1);
	const vars = query.split('&');

	for (var i = 0; i < vars.length; i++) {
		var pair = vars[i].split('=');
		if (pair[0] === variable) {
			return pair[1];
		}
	}
	return false;
}

export function getTokenExpirationDate() {
	return new Date(new Date().getTime() + MS_JWT_TOKEN_EXPIRATION);
}

export function statusToString(status) {
	switch (status) {
		case 'in_progress':
			return 'In progress';
		case 'pending_signature':
			return 'Pending signature';
		case 'approved':
			return 'Approved';
		case 'qsr':
			return 'QSR';
		default:
			return 'Unknown';
	}
}

export const getPrettyStatus = status => {
	const capitalized = status.charAt(0).toUpperCase() + status.slice(1);
	return capitalized.replace(/_/g, ' ');
};

// Return array of groups for authenticated user
const authGroupIds = auth => {
	if (!auth.groups || !auth.groups.length) {
		return [];
	}

	return auth.groups.map(g => g.id);
};

// Check if redux auth contains admin
export const isAdmin = auth => {
	const groupIds = authGroupIds(auth);

	return includes(groupIds, 1) || includes(groupIds, 2);
};

// Check if redux auth contains coordinator
export const isCoordinator = auth => {
	const groupIds = authGroupIds(auth);

	return includes(groupIds, GROUP_IDS.coordinator);
};

// Check if redux auth contains writer
export const isWriter = auth => {
	const groupIds = authGroupIds(auth);

	return includes(groupIds, GROUP_IDS.writer);
};

// Check if redux auth contains writer
export const isExternalWriter = auth => {
	const groupIds = authGroupIds(auth);

	return includes(groupIds, GROUP_IDS.externalWriter);
};

// Check if user is member of a group by id
export const isMemberOfGroup = (auth, groupId) => {
	const groupIds = authGroupIds(auth);

	return includes(groupIds, groupId);
};

// Return if user is member of any of the qsr department groups
export const isQSRDepartmentMember = auth =>
	some(auth.groups || [], group => includes(group.name, 'qsr/department'));

// Return the prio of an object casted to number
export const groupByPriority = obj => {
	const prio = Number(obj.priority || 1);

	if (prio > 2) return 3;
	if (prio > 1) return 2;
	return 1;
};

// Order a qsr list
const orderQSRList = list => {
	const rank = {
		draft: 1,
		question: 2,
		reported: 3,
		pending_back_end: 4,
		pending_writers: 5,
		in_progress: 6,
		implementation: 7,
		in_evaluation: 8,

		closed: 9,
	};

	return sortBy(list, item => {
		return rank[item.status] || -1;
	});
};

// Group qsr list
export const groupQSRList = (list, statusesToSplitByPrio = false) => {
	const byStatus = toPairs(groupBy(list, 'status'));

	// If not statuses to split by status, split all of them
	const shouldSplit = status =>
		statusesToSplitByPrio ? statusesToSplitByPrio.indexOf(status) !== -1 : true;

	const groupedList = byStatus.map(statusGroup => {
		const status = statusGroup[0];
		const items = statusGroup[1];

		if (shouldSplit(status)) {
			const groupedItems = groupBy(items, groupByPriority);

			const priorities = map(groupedItems, (items, i) => ({
				prio: i,
				items,
			}));

			return {
				status,
				priorities,
			};
		}

		return { status, items };
	});

	return orderQSRList(groupedList);
};

// Get end user options by active and inactive
export const getEndUserOptions = list => {
	const alphabetical = sortBy(list, 'name');

	const splitted = partition(alphabetical, ({ id }) => startsWith(id, '6'));

	const active = idx(splitted, _ => _[0]) || [];
	const inactive = idx(splitted, _ => _[1]) || [];

	const getOptions = item => ({
		value: item.id,
		label: `${item.name} (${item.id})`,
	});

	return {
		active: active.map(getOptions),
		inactive: inactive.map(getOptions),
	};
};

// Given a user, find a sortable value. Take first name if given, fallback to username
export const getUserOrderValue = user => {
	const name = idx(user, _ => _.first_name);
	const username = idx(user, _ => _.username) || '';

	if (name) return name.toLowerCase();

	return username.toLowerCase();
};

export const hideOrShow = (fromId, selected) => {
	if (fromId < selected || !selected) {
		const hide = {
			display: 'none',
		};
		return hide;
	} else return {};
};



// WEBPACK FOOTER //
// ./src/helpers.js